/**
 * Terms & conditions modal — redesign pass C (item 16).
 *
 * The shared site footer links to terms & conditions. Until legal supplies
 * the real document (or a dedicated page exists), this on-brand modal
 * carries a placeholder summary so the link is never dead — the same
 * pattern as PrivacyStatementModal.
 */

import { motion } from 'motion/react';
import { ScrollText, X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

// PLACEHOLDER — real content supplied later (legal-approved terms & conditions).
const PLACEHOLDER_PARAGRAPHS = [
  'Quotes prepared through this site are proposals, not binding orders. Nothing is produced or invoiced until you have confirmed the final proposal we send you.',
  'Prices shown in the builder are estimates excluding VAT; final pricing depends on quantities, branding requirements and delivery details and is confirmed in your proposal.',
  'Production starts only after written approval of the proposal and the branding proof. Delivery windows are agreed per order.',
];

export function TermsModal({ open, onClose }: Props) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close terms and conditions"
          className="absolute top-5 right-5 p-2 rounded-full text-gray-400 hover:text-[#356d4e] hover:bg-gray-50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-14 h-14 bg-[#57c858]/10 rounded-full flex items-center justify-center mb-6">
          <ScrollText className="w-7 h-7 text-[#356d4e]" />
        </div>
        <h3
          id="terms-title"
          className="text-2xl font-bold font-century text-[#444444] mb-4 flex items-baseline gap-2"
        >
          Terms &amp; conditions
          <span aria-hidden="true" className="w-2 h-2 gradient-dot" />
        </h3>
        <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
          {PLACEHOLDER_PARAGRAPHS.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-8 w-full py-4 bg-linear-to-br from-[#356d4e] to-[#57c858] text-white rounded-full font-bold font-century tracking-widest uppercase text-sm hover:opacity-95 transition-opacity shadow-lg"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
}
